import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { userAPI, analyticsAPI, examAPI } from '../../services/api';
import toast from 'react-hot-toast';

import { 
  FiBarChart, 
  FiCheckCircle, 
  FiTrendingUp, 
  FiAward, 
  FiUser, 
  FiBookOpen, 
  FiCalendar, 
  FiTarget, 
  FiFileText, 
  FiClock, 
  FiStar, 
  FiBarChart2
} from 'react-icons/fi';

const StudentDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [activeTab, setActiveTab] = useState('overview');
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  // Fetch real student data
  const { 
    data: studentProfile, 
    isLoading: profileLoading, 
    error: profileError 
  } = useQuery({
    queryKey: ['student-profile', user?.id],
    queryFn: () => userAPI.getProfile(),
    enabled: !!user?.id,
    refetchInterval: 60000, // Refetch every minute
  });

  const { 
    data: studentAnalytics, 
    isLoading: analyticsLoading, 
    error: analyticsError 
  } = useQuery({
    queryKey: ['student-analytics', user?.id, selectedPeriod],
    queryFn: () => analyticsAPI.getUserAnalytics(user?.id),
    enabled: !!user?.id,
    refetchInterval: 60000, // Refetch every minute
  });

  const { 
    data: recentAttempts, 
    isLoading: attemptsLoading, 
    error: attemptsError 
  } = useQuery({
    queryKey: ['recent-attempts', user?.id],
    queryFn: () => analyticsAPI.getRecentActivities(),
    enabled: !!user?.id,
    refetchInterval: 60000, // Refetch every minute
  });

  // Fetch ALL exam attempts for the student (for Recent Test Results section)
  const {
    data: fullHistory,
    isLoading: fullHistoryLoading,
    error: fullHistoryError
  } = useQuery({
    queryKey: ['all-history', user?.id],
    queryFn: () => examAPI.getUserExamHistory({ limit: 1000 }),
    enabled: !!user?.id,
    refetchInterval: 60000,
  });

  const { 
    data: upcomingExams, 
    isLoading: examsLoading, 
    error: examsError 
  } = useQuery({
    queryKey: ['upcoming-exams'],
    queryFn: () => examAPI.getUpcomingExams(),
    refetchInterval: 60000, // Refetch every minute
  });

  // Extract data from API responses
  const profile = studentProfile?.data?.data?.user || {};
  const analytics = studentAnalytics?.data?.data || {};
  const attempts = recentAttempts?.data?.data?.recentAttempts || [];
  const allAttempts = fullHistory?.data?.data?.attempts || [];
  const currentUserIds = [user?.id, user?.userId, profile?.id, profile?.studentId].filter(Boolean);
  const exams = upcomingExams?.data?.data?.exams || [];

  // Calculate derived stats from user analytics
  const totalTests = analytics.overview?.totalAttempts || 0;
  const averageScore = analytics.overview?.averageScore || 0;
  const completedTests = analytics.overview?.completedAttempts || 0;
  const completionRate = analytics.overview?.completionRate || 0;
  const completionRateDisplay = `${Math.round((Number(completionRate) || 0) * 10) / 10}%`;
  const certificates = 0; // Not available in current API
  const streak = 0; // Not available in current API

  const stats = [
    { id: 1, title: 'Overall Score', value: '', change: '+5%', icon: <FiBarChart size={32} />, color: 'primary', trend: 'up' }, // Commented out score value
    { id: 2, title: 'Tests Completed', value: `${completedTests}/${totalTests}`, change: `${completionRateDisplay}`, icon: <FiCheckCircle size={32} />, color: 'success', trend: 'up' },
    { id: 3, title: 'Study Streak', value: `${streak} days`, change: '+2', icon: <FiTrendingUp size={32} />, color: 'warning', trend: 'up' },
    { id: 4, title: 'Certificates', value: `${certificates}`, change: '+1', icon: <FiAward size={32} />, color: 'danger', trend: 'up' }
  ];

  // Transform recent attempts to match UI format
  // Show only the LAST test result in Recent Test Results section
  const recentTests = (allAttempts.length ? allAttempts : attempts)
    .filter(attempt => {
      const attemptUserId = attempt.userId || attempt.user?.id;
      return currentUserIds.length === 0 ? true : currentUserIds.includes(attemptUserId);
    })
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)) // Sort by most recent first
    .slice(0, 1) // Take only the first (most recent) result
    .map(attempt => ({
    id: attempt.id,
    title: attempt.exam?.title || 'Unknown Exam',
    category: attempt.exam?.examCategory?.name || 'Unknown Category',
      score: typeof attempt.obtainedMarks === 'number' ? attempt.obtainedMarks : (attempt.examScore?.obtainedMarks || 0),
      maxScore: attempt.totalMarks || attempt.exam?.totalMarks || attempt.examScore?.totalMarks || 0,
      percentage: (() => {
        const obtained = typeof attempt.obtainedMarks === 'number' ? attempt.obtainedMarks : (attempt.examScore?.obtainedMarks || 0);
        const total = attempt.totalMarks || attempt.exam?.totalMarks || attempt.examScore?.totalMarks || 0;
        return total > 0 ? Math.round((obtained / total) * 100) : (attempt.percentage || 0);
      })(),
    date: new Date(attempt.createdAt).toLocaleDateString(),
    status: attempt.isPassed ? 'passed' : (attempt.status?.toLowerCase() || 'completed'),
      timeSpent: `${attempt.timeSpent ? Math.round(attempt.timeSpent / 60) : 0} min`
  })) || [];

  // Transform upcoming exams to match UI format
  const upcomingTests = exams.map(exam => ({
    id: exam.id,
    title: exam.title,
    category: exam.examCategory?.name || 'Unknown Category',
    date: new Date(exam.scheduledStart).toLocaleDateString(),
    time: new Date(exam.scheduledStart).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    duration: `${exam.duration} min`,
    questions: exam.questionCount || 0
  })) || [];

  // Category performance from analytics
  const categoryPerformance = analytics.performance?.categoryPerformance?.map(cat => ({
    category: cat.categoryName || 'Unknown',
    tests: cat.attemptCount || 0,
    avgScore: cat.averageScore || 0,
    improvement: '+8%', // This would need to be calculated from historical data
    color: 'var(--primary-500)'
  })) || [];

  // Achievements from analytics (not available in current API, using mock data)
  const achievements = [
    {
      id: 1,
      title: 'First Test',
      description: 'Completed your first exam',
      icon: <FiTarget size={24} />,
      date: new Date().toLocaleDateString(),
      type: 'milestone'
    },
    {
      id: 2,
      title: 'Perfect Score',
      description: 'Achieved 100% on an exam',
      icon: <FiStar size={24} />,
      date: new Date().toLocaleDateString(),
      type: 'achievement'
    }
  ];

  // Handle errors
  if (profileError || analyticsError || attemptsError || examsError || fullHistoryError) {
    return (
      <div className="error-container">
        <h3>Error loading dashboard</h3>
        <p>Failed to load student data. Please try again later.</p>
        <button className="btn btn-primary" onClick={() => window.location.reload()}>
          Retry
        </button>
      </div>
    );
  }

  const isLoading = profileLoading || analyticsLoading || attemptsLoading || examsLoading || fullHistoryLoading;

  const getScoreColor = (score) => {
    if (score >= 90) return 'var(--success-500)';
    if (score >= 80) return 'var(--warning-500)';
    if (score >= 70) return 'var(--danger-500)';
    return 'var(--secondary-500)';
  };

  const getStatusColor = (status) => {
    const colors = {
      completed: 'var(--success-500)',
      pending: 'var(--warning-500)',
      failed: 'var(--danger-500)'
    };
    return colors[status] || 'var(--secondary-500)';
  };

  return (
    <div>
      {/* Hero Section */}
      <div className="student-hero" style={{
        background: 'linear-gradient(135deg, var(--success-600), var(--success-700))',
        borderRadius: '24px',
        padding: isMobile ? '20px' : '40px',
        marginBottom: '40px',
        color: 'white',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{ position: 'relative', zIndex: 2 }}>
          <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? '20px' : '0px', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'flex-start', marginBottom: '32px' }}>
            <div>
              <h1 style={{ fontSize: isMobile ? '24px' : '36px', fontWeight: '700', marginBottom: '12px' }}>
                {isLoading ? 'Loading...' : `Welcome back, ${profile.firstName || 'Student'}!`} <FiBookOpen size={isMobile ? 24 : 36} style={{ display: 'inline', marginLeft: '8px' }} />
              </h1>
              <p style={{ fontSize: isMobile ? '14px' : '18px', opacity: 0.9, marginBottom: '24px' }}>
                {isLoading ? 'Loading your progress...' : 'Keep up the great work! You\'re making excellent progress in your studies.'}
              </p>
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                <div style={{
                  padding: '8px 12px',
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  borderRadius: '8px',
                  backdropFilter: 'blur(10px)',
                  fontSize: '12px',
                  fontWeight: '500',
                  minWidth: 'fit-content',
                  flex: '1 1 auto'
                }}>
                  <FiUser size={14} style={{ display: 'inline', marginRight: '6px' }} />
                  {isLoading ? 'Loading...' : (profile.studentId || profile.id || 'N/A')}
                </div>
                <div style={{
                  padding: '8px 12px',
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  borderRadius: '8px',
                  backdropFilter: 'blur(10px)',
                  fontSize: '12px',
                  fontWeight: '500',
                  minWidth: 'fit-content',
                  flex: '1 1 auto',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  <FiCalendar size={14} style={{ display: 'inline', marginRight: '6px', flexShrink: 0 }} />
                  <span style={{ whiteSpace: 'nowrap' }}>Member since {isLoading ? 'Loading...' : (profile.joinDate ? new Date(profile.joinDate).toLocaleDateString() : 'N/A')}</span>
                </div>
                <div style={{
                  padding: '8px 12px',
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  borderRadius: '8px',
                  backdropFilter: 'blur(10px)',
                  fontSize: '12px',
                  fontWeight: '500',
                  minWidth: 'fit-content',
                  flex: '1 1 auto'
                }}>
                  <FiTarget size={14} style={{ display: 'inline', marginRight: '6px' }} />
                  Level: {isLoading ? 'Loading...' : (profile.level || 'Beginner')}
                </div>
              </div>
            </div>
            
            <div style={{
              width: isMobile ? '80px' : '120px',
              height: isMobile ? '80px' : '120px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0.1))',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: isMobile ? '32px' : '48px',
              backdropFilter: 'blur(10px)',
              border: '3px solid rgba(255, 255, 255, 0.3)'
            }}>
              {isLoading ? <FiClock size={isMobile ? 32 : 48} /> : (profile.avatar ? <FiUser size={isMobile ? 32 : 48} /> : <FiBookOpen size={isMobile ? 32 : 48} />)}
            </div>
          </div>

          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              style={{
                padding: '12px 20px',
                border: 'none',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: '500',
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                cursor: 'pointer',
                backdropFilter: 'blur(10px)'
              }}
            >
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="quarter">This Quarter</option>
              <option value="year">This Year</option>
            </select>
            
            <button 
              onClick={() => navigate('/student/tests')}
              style={{
                padding: '12px 24px',
                border: 'none',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: '600',
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                cursor: 'pointer',
                backdropFilter: 'blur(10px)',
                transition: 'all 0.3s ease'
              }}
            >
              <FiFileText size={16} style={{ display: 'inline', marginRight: '8px' }} />
              Take New Test
            </button>
          </div>
        </div>
        
        {/* Background decoration */}
        <div style={{
          position: 'absolute',
          top: '-50%',
          right: '-20%',
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.1)',
          zIndex: 1
        }}></div>
        <div style={{
          position: 'absolute',
          bottom: '-30%',
          left: '-10%',
          width: '200px',
          height: '200px',
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.05)',
          zIndex: 1
        }}></div>
      </div>

      {/* Stats Grid */}
      <div className="dashboard-grid" style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px' }}>
        {isLoading ? (
          // Loading skeleton for stats
          [1, 2, 3, 4].map(i => (
            <div key={i} className="dashboard-card" style={{
              background: 'linear-gradient(135deg, white 0%, var(--secondary-50) 100%)',
              border: '2px solid var(--secondary-200)',
              borderRadius: '16px',
              padding: '24px',
              boxShadow: 'var(--shadow-lg)',
              position: 'relative',
              overflow: 'hidden'
            }}>
              <div className="dashboard-card-header">
                <h3 className="dashboard-card-title">Loading...</h3>
                <div className="dashboard-card-icon primary">⏳</div>
              </div>
              <div className="dashboard-card-value">--</div>
              <p className="dashboard-card-description">Loading data...</p>
            </div>
          ))
        ) : (
          stats.map((stat) => (
          <div key={stat.id} className="dashboard-card" style={{
            background: 'linear-gradient(135deg, white 0%, var(--secondary-50) 100%)',
            border: '2px solid var(--secondary-200)'
          }}>
            <div className="dashboard-card-header">
              <h3 className="dashboard-card-title">{stat.title}</h3>
              <div className={`dashboard-card-icon ${stat.color}`} style={{
                fontSize: '32px',
                width: '80px',
                height: '80px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {stat.icon}
              </div>
            </div>
            <div className="dashboard-card-value" style={{ fontSize: '48px' }}>{stat.value}</div>
            <p className="dashboard-card-description" style={{ fontSize: '16px' }}>
              <span style={{ color: 'var(--success-600)', fontWeight: '600' }}>
                {stat.change}
              </span> from last period
            </p>
          </div>
          ))
        )}
      </div>

      {/* Tabs Section */}
      <div style={{
        background: 'white',
        borderRadius: '20px',
        padding: isMobile ? '20px' : '32px',
        marginBottom: '40px',
        boxShadow: 'var(--shadow-lg)',
        border: '2px solid var(--secondary-200)'
      }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '32px', borderBottom: '2px solid var(--secondary-200)', paddingBottom: '16px' }}>
          {['overview', 'performance', 'achievements'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                padding: isMobile ? '10px 16px' : '12px 24px',
                border: 'none',
                borderRadius: '12px',
                fontSize: isMobile ? '14px' : '16px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                textTransform: 'capitalize',
                backgroundColor: activeTab === tab ? 'var(--success-500)' : 'var(--secondary-100)',
                color: activeTab === tab ? 'white' : 'var(--secondary-700)'
              }}
            >
              {tab === 'overview' && <><FiBarChart2 size={16} style={{ display: 'inline', marginRight: '8px' }} />Overview</>}
              {tab === 'performance' && <><FiTrendingUp size={16} style={{ display: 'inline', marginRight: '8px' }} />Performance</>}
              {tab === 'achievements' && <><FiAward size={16} style={{ display: 'inline', marginRight: '8px' }} />Achievements</>}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr', gap: '32px' }}>
            {/* Recent Tests */}
            <div>
              <h3 style={{ fontSize: '24px', fontWeight: '600', color: 'var(--secondary-900)', marginBottom: '24px' }}>
                Recent Test Results
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {isLoading ? (
                  // Loading skeleton for recent tests
                  [1, 2, 3].map(i => (
                    <div key={i} style={{
                      display: 'flex',
                      alignItems: isMobile ? 'flex-start' : 'center',
                      flexDirection: isMobile ? 'column' : 'row',
                      gap: '16px',
                      padding: '20px',
                      borderRadius: '16px',
                      backgroundColor: 'var(--secondary-50)',
                      border: '2px solid var(--secondary-200)',
                      transition: 'all 0.3s ease'
                    }}>
                      <div style={{
                        width: '60px',
                        height: '60px',
                        borderRadius: '12px',
                        backgroundColor: 'var(--secondary-300)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '24px',
                        color: 'white',
                        fontWeight: '700'
                      }}>
                        --
                      </div>
                      <div style={{ flex: 1 }}>
                        <h4 style={{ fontSize: isMobile ? '16px' : '18px', fontWeight: '600', color: 'var(--secondary-900)', marginBottom: '8px' }}>
                          <span><FiBookOpen size={14} style={{ display: 'inline', marginRight: '4px' }} />Loading...</span>
                        </h4>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', fontSize: isMobile ? '12px' : '14px', color: 'var(--secondary-600)' }}>
                          <span><FiBookOpen size={14} style={{ display: 'inline', marginRight: '4px' }} />Loading...</span>
                          <span><FiCalendar size={14} style={{ display: 'inline', marginRight: '4px' }} />Loading...</span>
                          <span><FiClock size={14} style={{ display: 'inline', marginRight: '4px' }} />Loading...</span>
                        </div>
                      </div>
                      <div style={{
                        padding: '8px 16px',
                        borderRadius: '8px',
                        backgroundColor: 'var(--secondary-300)',
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: '600',
                        textTransform: 'uppercase'
                      }}>
                        Loading
                      </div>
                    </div>
                  ))
                ) : recentTests.length === 0 ? (
                  <div style={{
                    padding: '40px',
                    textAlign: 'center',
                    color: 'var(--secondary-600)',
                    fontSize: '16px'
                  }}>
                    No recent test results found. Take your first test to see your results here!
                  </div>
                ) : (
                  recentTests.map((test) => (
                  <div key={test.id} style={{
                    display: 'flex',
                    alignItems: isMobile ? 'flex-start' : 'center',
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: '16px',
                    padding: '20px',
                    borderRadius: '16px',
                    backgroundColor: 'var(--secondary-50)',
                    border: '2px solid var(--secondary-200)',
                    transition: 'all 0.3s ease'
                  }}>
                    <div style={{
                      width: '60px',
                      height: '60px',
                      borderRadius: '12px',
                      backgroundColor: getScoreColor(test.percentage || 0),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '16px',
                      color: 'white',
                      fontWeight: '700',
                      flexDirection: 'column',
                      lineHeight: 1.1
                    }}>
                      <span>{test.score}</span>
                      <span style={{ fontSize: '12px', opacity: 0.9 }}>/ {test.maxScore}</span>
                    </div>
                    <div style={{ flex: 1 }}>
                      <h4 style={{ fontSize: isMobile ? '16px' : '18px', fontWeight: '600', color: 'var(--secondary-900)', marginBottom: '8px' }}>
                        {test.title}
                      </h4>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', fontSize: isMobile ? '12px' : '14px', color: 'var(--secondary-600)' }}>
                        <span><FiBookOpen size={14} style={{ display: 'inline', marginRight: '4px' }} />{test.category}</span>
                        <span><FiCalendar size={14} style={{ display: 'inline', marginRight: '4px' }} />{test.date}</span>
                        <span><FiClock size={14} style={{ display: 'inline', marginRight: '4px' }} />{test.timeSpent}</span>
                        <span>Score: {test.percentage}%</span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginLeft: 'auto' }}>
                      <span style={{
                        padding: '8px 12px',
                        borderRadius: '8px',
                        backgroundColor: getStatusColor(test.status),
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: '600',
                        textTransform: 'uppercase'
                      }}>
                        {test.status}
                      </span>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => navigate(`/exam/results/${test.id}`)}
                      >
                        View Result
                      </button>
                    </div>
                  </div>
                  ))
                )}
              </div>
            </div>

            {/* Upcoming Tests */}
            <div>
              <h3 style={{ fontSize: '24px', fontWeight: '600', color: 'var(--secondary-900)', marginBottom: '24px' }}>
                Upcoming Tests
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {isLoading ? (
                  // Loading skeleton for upcoming tests
                  [1, 2].map(i => (
                    <div key={i} style={{
                      padding: '20px',
                      borderRadius: '16px',
                      backgroundColor: 'var(--secondary-50)',
                      border: '2px solid var(--secondary-200)',
                      transition: 'all 0.3s ease'
                    }}>
                      <h4 style={{ fontSize: '16px', fontWeight: '600', color: 'var(--secondary-900)', marginBottom: '12px' }}>
                        <span><FiBookOpen size={14} style={{ display: 'inline', marginRight: '4px' }} />Loading...</span>
                      </h4>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ fontSize: '14px', color: 'var(--secondary-600)' }}>Category:</span>
                          <span style={{ fontSize: '14px', fontWeight: '500', color: 'var(--success-600)' }}>Loading...</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ fontSize: '14px', color: 'var(--secondary-600)' }}>Date:</span>
                          <span style={{ fontSize: '14px', fontWeight: '500', color: 'var(--secondary-900)' }}>Loading...</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ fontSize: '14px', color: 'var(--secondary-600)' }}>Time:</span>
                          <span style={{ fontSize: '14px', fontWeight: '500', color: 'var(--secondary-900)' }}>Loading...</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ fontSize: '14px', color: 'var(--secondary-600)' }}>Duration:</span>
                          <span style={{ fontSize: '14px', fontWeight: '500', color: 'var(--warning-600)' }}>Loading...</span>
                        </div>
                      </div>
                      <button className="btn btn-primary btn-sm" style={{ width: '100%' }} disabled>
                        <FiClock size={16} style={{ display: 'inline', marginRight: '8px' }} />Loading...
                      </button>
                    </div>
                  ))
                ) : upcomingTests.length === 0 ? (
                  <div style={{
                    padding: '40px',
                    textAlign: 'center',
                    color: 'var(--secondary-600)',
                    fontSize: '16px'
                  }}>
                    No upcoming tests scheduled. Check back later for new tests!
                  </div>
                ) : (
                  upcomingTests.map((test) => (
                  <div key={test.id} style={{
                    padding: '20px',
                    borderRadius: '16px',
                    backgroundColor: 'var(--secondary-50)',
                    border: '2px solid var(--secondary-200)',
                    transition: 'all 0.3s ease'
                  }}>
                    <h4 style={{ fontSize: '16px', fontWeight: '600', color: 'var(--secondary-900)', marginBottom: '12px' }}>
                      {test.title}
                    </h4>
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '8px', marginBottom: '16px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: '14px', color: 'var(--secondary-600)' }}>Category:</span>
                        <span style={{ fontSize: '14px', fontWeight: '500', color: 'var(--success-600)' }}>{test.category}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: '14px', color: 'var(--secondary-600)' }}>Date:</span>
                        <span style={{ fontSize: '14px', fontWeight: '500', color: 'var(--secondary-900)' }}>{test.date}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: '14px', color: 'var(--secondary-600)' }}>Time:</span>
                        <span style={{ fontSize: '14px', fontWeight: '500', color: 'var(--secondary-900)' }}>{test.time}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: '14px', color: 'var(--secondary-600)' }}>Duration:</span>
                        <span style={{ fontSize: '14px', fontWeight: '500', color: 'var(--warning-600)' }}>{test.duration}</span>
                      </div>
                    </div>
                    <button className="btn btn-primary btn-sm" style={{ width: '100%' }}>
                      <FiFileText size={16} style={{ display: 'inline', marginRight: '8px' }} />Start Preparation
                    </button>
                  </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'performance' && (
          <div>
            <h3 style={{ fontSize: '24px', fontWeight: '600', color: 'var(--secondary-900)', marginBottom: '24px' }}>
              Category Performance
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
              {categoryPerformance.map((cat, index) => (
                <div key={index} style={{
                  padding: '24px',
                  borderRadius: '16px',
                  backgroundColor: 'var(--secondary-50)',
                  border: '2px solid var(--secondary-200)',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    backgroundColor: cat.color
                  }}></div>
                  
                  <h4 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--secondary-900)', marginBottom: '16px' }}>
                    {cat.category}
                  </h4>
                  
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                    <span style={{ fontSize: '14px', color: 'var(--secondary-600)' }}>Tests Taken:</span>
                    <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--primary-600)' }}>{cat.tests}</span>
                  </div>
                  
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                    <span style={{ fontSize: '14px', color: 'var(--secondary-600)' }}>Average Score:</span>
                    <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--success-600)' }}>{cat.avgScore}%</span>
                  </div>
                  
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '14px', color: 'var(--secondary-600)' }}>Improvement:</span>
                    <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--success-600)' }}>{cat.improvement}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'achievements' && (
          <div>
            <h3 style={{ fontSize: '24px', fontWeight: '600', color: 'var(--secondary-900)', marginBottom: '24px' }}>
              Recent Achievements
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
              {achievements.map((achievement) => (
                <div key={achievement.id} style={{
                  padding: '24px',
                  borderRadius: '16px',
                  backgroundColor: 'var(--secondary-50)',
                  border: '2px solid var(--secondary-200)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  transition: 'all 0.3s ease'
                }}>
                  <div style={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, var(--success-500), var(--success-600))',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '24px',
                    color: 'white'
                  }}>
                    {achievement.icon}
                  </div>
                  <div style={{ flex: 1 }}>
                    <h4 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--secondary-900)', marginBottom: '8px' }}>
                      {achievement.title}
                    </h4>
                    <p style={{ fontSize: '14px', color: 'var(--secondary-600)', marginBottom: '8px' }}>
                      {achievement.description}
                    </p>
                    <span style={{ fontSize: '12px', color: 'var(--secondary-500)' }}>
                      <FiClock size={12} style={{ display: 'inline', marginRight: '4px' }} />{achievement.date}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>



      {/* Quick Actions */}
      <div style={{
        background: 'white',
        borderRadius: '20px',
        padding: isMobile ? '20px' : '32px',
        boxShadow: 'var(--shadow-lg)',
        border: '2px solid var(--secondary-200)'
      }}>
        <h3 style={{ fontSize: isMobile ? '20px' : '24px', fontWeight: '600', color: 'var(--secondary-900)', marginBottom: '24px' }}>
          Quick Actions
        </h3>
        
        {/* Test Notification Button removed as requested */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
          <button
            onClick={() => navigate('/student/tests')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              padding: isMobile ? '16px' : '24px',
              border: '2px solid var(--secondary-200)',
              borderRadius: '16px',
              backgroundColor: 'var(--secondary-50)',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              textAlign: 'left',
              width: '100%'
            }}
          >
            <div style={{
              width: isMobile ? '40px' : '48px',
              height: isMobile ? '40px' : '48px',
              borderRadius: '12px',
              backgroundColor: 'var(--primary-500)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: isMobile ? '18px' : '20px',
              color: 'white'
            }}>
              <FiFileText size={isMobile ? 20 : 24} />
            </div>
            <div>
              <h4 style={{ fontSize: isMobile ? '14px' : '16px', fontWeight: '600', color: 'var(--secondary-900)', marginBottom: '4px' }}>
                Take New Test
              </h4>
              <p style={{ fontSize: isMobile ? '12px' : '14px', color: 'var(--secondary-600)', margin: 0 }}>
                Browse available tests
              </p>
            </div>
          </button>

          {/* View History button commented out as requested */}
          {/**
          <button
            onClick={() => navigate('/student/history')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              padding: isMobile ? '16px' : '24px',
              border: '2px solid var(--secondary-200)',
              borderRadius: '16px',
              backgroundColor: 'var(--secondary-50)',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              textAlign: 'left',
              width: '100%'
            }}
          >
            <div style={{
              width: isMobile ? '40px' : '48px',
              height: isMobile ? '40px' : '48px',
              borderRadius: '12px',
              backgroundColor: 'var(--success-500)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: isMobile ? '18px' : '20px',
              color: 'white'
            }}>
              <FiBarChart2 size={isMobile ? 20 : 24} />
            </div>
            <div>
              <h4 style={{ fontSize: isMobile ? '14px' : '16px', fontWeight: '600', color: 'var(--secondary-900)', marginBottom: '4px' }}>
                View History
              </h4>
              <p style={{ fontSize: isMobile ? '12px' : '14px', color: 'var(--secondary-600)', margin: 0 }}>
                Check past results
              </p>
            </div>
          </button>
          **/}

          <button
            onClick={() => navigate('/student/profile')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              padding: isMobile ? '16px' : '24px',
              border: '2px solid var(--secondary-200)',
              borderRadius: '16px',
              backgroundColor: 'var(--secondary-50)',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              textAlign: 'left',
              width: '100%'
            }}
          >
            <div style={{
              width: isMobile ? '40px' : '48px',
              height: isMobile ? '40px' : '48px',
              borderRadius: '12px',
              backgroundColor: 'var(--warning-500)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: isMobile ? '18px' : '20px',
              color: 'white'
            }}>
              <FiUser size={isMobile ? 20 : 24} />
            </div>
            <div>
              <h4 style={{ fontSize: isMobile ? '14px' : '16px', fontWeight: '600', color: 'var(--secondary-900)', marginBottom: '4px' }}>
                Update Profile
              </h4>
              <p style={{ fontSize: isMobile ? '12px' : '14px', color: 'var(--secondary-600)', margin: 0 }}>
                Manage your account
              </p>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default StudentDashboard; 